import { AdminAreaChainItem } from "../../../../utils/adminArea";
import type { DeliveryCostSide } from "../../../../utils/delivery-cost";
import { Deposit } from "../../../Deposits/DepositCard/deposit-dtos";
import { NegotiationProposal } from "../OfferCommentsModal/NegotiatedDetailsCard/offer-negotiation-proposal";
import { OfferCommentDto } from "../OfferCommentsModal/OfferCommentCard";

export interface NameTranslations {
  en: string;
}

export interface BuyCommodity {
  id: number;
  name_translations: NameTranslations;
  name: string;
  wings_id: string;
  countries: number[];
  units: number[];
}

export interface Currency {
  id: number;
  code: string;
  symbol: string;
  name: string;
}

export interface UnitOfMeasure {
  id: number;
  name: string;
  code: string;
  type: string;
}

export interface Trader {
  id: number;
  name: string;
  contact_email: string;
}

export interface Person {
  first_name: string;
  middle_name: string;
  last_name: string;
  contact_phone: string;
}

export interface TraderRepresentative {
  id: number;
  person: Person;
  position: string;
  ciam_user_created: boolean;
  terms_of_service_accepted_date: Date;
  login_user: number;
}

export interface CustomDeliveryLocationDetails {
  active: boolean;
  id: number;
  location: number;
  name: string;
  name_translations: Record<string, string>;
  physical_address: string;
}

export interface QualityGrade {
  id: number;
  code: string;
  name: string;
  name_translations: Record<string, string>;
  active: boolean;
}

export interface Offer {
  id: number;
  demand?: number | null;
  buy_commodity: BuyCommodity;
  currency: Currency;
  // Strings as served by the API; numbers where the app rebuilds an offer
  // from a negotiation proposal. Read through formatNumber()/Number().
  price: string | number;
  delivery_cost?: string | number | null;
  minimum_quantity: string | number;
  maximum_quantity: string | number;
  negotiable: boolean;
  unit_of_measure: UnitOfMeasure;
  quality: string;
  quality_grade?: QualityGrade | null;
  is_school_offer?: boolean;
  demand_costs?: {
    transport_cost: string | number;
  } | null;
  last_update_datetime: string;
  status: OfferStatus;
  offer_type: string;
  valid_until: string;
  trader: Trader;
  orders: {
    deposit: Deposit;
  }[];
  deposit?: Deposit;
  trader_representative: TraderRepresentative;
  tagged_aggregator: {
    id: number;
    name: string;
  };
  tagged_aggregator_admin_area?: AdminAreaChainItem[] | null;
  publishing_date: string;
  publishing_datetime?: string;
  delivery_location: string;
  comment?: string;
  contract_number: string;
  planned_exchange_time?: string;
  exchange_confirmation_time?: string;
  latest_negotiation_proposal: NegotiationProposal;
  latest_comment: OfferCommentDto;
  comment_count?: number;
  payment_time_option?: OfferPaymentTimeOption;
  custom_delivery_location_details?: CustomDeliveryLocationDetails;
  custom_delivery_location?: number;
  custom_delivery_location_type?: number;
  exchange?: OfferExchange | null;
  delivery_note?: string | null;
  original_terms?: OfferOriginalTerms | null;
  // Demand lines only: whether this line's payment also covered the demand's delivery cost.
  includes_delivery_cost?: boolean;
  // The demand's delivery-cost state, on demand lines of the requester's own organisation.
  demand_terms?: DemandTerms | null;
}

export type DeliveryCostProposalStatus = "PENDING" | "ACCEPTED" | "COUNTERED" | "WITHDRAWN";

/** One amount proposed for a demand's delivery cost, by the aggregator or the buyer. */
export interface DeliveryCostProposal {
  id: number;
  delivery_cost: string;
  side: DeliveryCostSide;
  status: DeliveryCostProposalStatus;
  text: string;
  proposed_by_name: string | null;
  created_datetime: string;
  resolved_datetime: string | null;
}

/** The demand-level delivery cost a demand line carries: one cost, negotiated and paid once. */
export interface DemandTerms {
  id: number;
  reference: number;
  negotiable: boolean;
  delivery_cost: string | null;
  original_delivery_cost: string | null;
  delivery_cost_locked: boolean;
  delivery_cost_paid: boolean;
  delivery_cost_paid_offer: number | null;
  // Lines still NEW or NEGOTIATING; closing the last one locks the cost.
  standing_lines: number;
  pending_delivery_cost_proposal: DeliveryCostProposal | null;
}

/** GET /api/market_demands/{id}/: the demand with its lines and full delivery-cost history, oldest first. */
export interface MarketDemandDeliveryCost extends DemandTerms {
  created_datetime: string;
  child_offers: Offer[];
  delivery_cost_proposals: DeliveryCostProposal[];
}

export interface OfferOriginalTerms {
  price: string | number;
  minimum_quantity: string | number;
  maximum_quantity: string | number;
  delivery_cost?: string | number | null;
  delivery_location: string;
  custom_delivery_location?: number | null;
  custom_delivery_location_type?: number | null;
  planned_exchange_time?: string | null;
}

export interface CommodityExchange {
  uuid: string;
  deposit_uuid: string;
  quantity: string;
  unit_of_measure: Pick<UnitOfMeasure, "id" | "code" | "name">;
  price: string;
  total_paid: string;
  currency: Pick<Currency, "id" | "code" | "symbol">;
  comment: string | null;
  confirmed_at: string;
}

export interface OfferExchange {
  source: string;
  is_complete: boolean;
  confirmed_at: string;
  actual_total_quantity: string;
  unit_of_measure: Pick<UnitOfMeasure, "id" | "code" | "name">;
  payment_method_details: { id: number; name: string } | null;
  payment_scope: string;
  payment_status: string;
  actual_total_paid: string;
  outstanding_balance: string | null;
  comment: string | null;
  delivery_note: string | null;
  commodity_exchanges: CommodityExchange[];
  deliveries: unknown[];
}

export enum OfferStatus {
  New = "NEW",
  Accepted = "ACCEPTED",
  Rejected = "REJECTED",
  OfferDone = "READY_FOR_EXCHANGE",
  ExchangeAgreed = "EXCHANGE_AGREED",
  ExchangeDone = "EXCHANGE_DONE",
  Recalled = "RECALLED",
  Negotiating = "NEGOTIATING",
  ExchangeDetailsUpdated = "EXCHANGE_DETAILS_UPDATED",
  ExchangeDetailsDisputed = "EXCHANGE_DETAILS_DISPUTED",
  ExchangePaymentPending = "EXCHANGE_PAYMENT_PENDING",
}

export enum OfferPaymentTimeOption {
  ReceiveUponExchange = "RECEIVE_UPON_EXCHANGE",
  ReceiveOnFutureDate = "RECEIVE_ON_FUTURE_DATE",
}
